import { MediaControlOrigin } from "../../events/PlayerEvent.js";
import { GenericFormatLabel, ParsedFormat } from "./FormatAndSizePreference.js";
import { VideoRef, MediaResourceStats } from "../MediaResource.js";
import { VideoContext } from "../Player.js";

/**
 * A wrapper class for the {@link VideoContext} interface that delegates all method and property calls
 * to an underlying {@link VideoContext} instance. This allows for selective overriding of behavior
 * while maintaining the original context's functionality.
 *
 * @remarks
 * Extend this class to override specific methods or properties as needed, while preserving
 * the default delegation for all other members.
 *
 * @example
 * ```typescript
 * class CustomVideoContextOverrider extends VideoContextOverrider {
 *   // Override specific methods or properties here
 * }
 * ```
 *
 * @param originalContext - The {@link VideoContext} instance to delegate calls to.
 */
export class VideoContextOverrider implements VideoContext {


    constructor(protected originalContext: VideoContext) {

    }
    reloadSource(keepCurrentTime: boolean): Promise<void> {
        return this.originalContext.reloadSource(keepCurrentTime)
    }
    get ref(): VideoRef {
        return this.originalContext.ref
    }

    get ended(): boolean {
        return this.originalContext.ended
    }

    toggleFullscreen(fullscreen: boolean): boolean {
        return this.originalContext.toggleFullscreen(fullscreen);
    }
    getAvailableQualities(): ParsedFormat[] {
        return this.originalContext.getAvailableQualities();
    }
    reset(): Promise<any> {
        return this.originalContext.reset()
    }
    pause(origin: MediaControlOrigin): Promise<any> {
        return this.originalContext.pause(origin)
    }

    get isPaused(): boolean {
        return this.originalContext.isPaused;
    }
    destroy() {
        return this.originalContext.destroy()
    }
    getCurrentFormat(): ParsedFormat {
        return this.originalContext.getCurrentFormat();
    }
  
    get stats(): MediaResourceStats {
        return this.originalContext.stats;
    }
   
    get currentTime(): number {
        return this.originalContext.currentTime;
    }
    get duration(): number {
        return this.originalContext.duration;
    }
    get metadataPromise(): Promise<void> {
        return this.originalContext.metadataPromise;
    }
    beforeUnload(): Promise<void> {
        return this.originalContext.beforeUnload();
    }
    autoplay(autoplay?: boolean): boolean {
        return this.originalContext.autoplay(autoplay);
    }
    show(): void {
        return this.originalContext.show();
    }
    play(origin: MediaControlOrigin): Promise<any> {
        return this.originalContext.play(origin);
    }
    fetchLastMediaControlOrigin(): MediaControlOrigin {
        return this.originalContext.fetchLastMediaControlOrigin();
    }
    restart(): void {
        return this.originalContext.restart();
    }

    togglePlay(manualPlay: boolean): Promise<boolean> {
        return this.originalContext.togglePlay(manualPlay);
    }
    toggleAudio(): Promise<boolean> {
        return this.originalContext.toggleAudio();
    }

    seek(time: number): Promise<boolean> {
        return this.originalContext.seek(time);
    }
    unmute(): Promise<boolean> {
        return this.originalContext.unmute();
    }
    muted(enable?: boolean): Promise<boolean> {
        return this.originalContext.muted(enable);
    }
    volume(volume?: number): number {
        return this.originalContext.volume(volume);
    }
    switchQuality(format: GenericFormatLabel): Promise<ParsedFormat> {
        return this.originalContext.switchQuality(format);
    }
    on(eventName: "ended" | "progress" | "timeupdate" | "pause" | "play" | "complete" | "waiting" | "stalled" | "volumechange" | "playing", listener: EventListener): void {
        return this.originalContext.on(eventName, listener);
    }
    off(eventName: "ended" | "progress" | "timeupdate" | "pause" | "play" | "complete" | "waiting" | "stalled" | "volumechange" | "playing", listener: EventListener): void {
        return this.originalContext.off(eventName, listener);
    }
    once(eventName: "ended" | "progress" | "timeupdate" | "pause" | "play" | "complete" | "waiting" | "stalled" | "volumechange" | "playing", listener: EventListener): void {
        return this.originalContext.once(eventName, listener);
    }

}